import type { PowurOsEnergyBackupData } from "@logic/lib/powur-os/models/module-data"
import { accountAnalysisSchema } from "@pacts/lib/genability/models"
import { z } from "zod"

import type {
  Proposal,
  LoanProduct,
  UtilityRate,
  Quote,
} from "../../prisma/generated"
import type { EstimateNecessaryFields } from "../design"
import { dateSchema } from "../generic"

export const savingsAnalysisSchema = z.object({
  id: z.string().optional().nullable(),
  createdAt: dateSchema.optional().nullable(),
  updatedAt: dateSchema.optional().nullable(),
  designId: z.string().or(z.number()).optional().nullable(),
  outputs: accountAnalysisSchema,
})
export type SavingsAnalysis = z.infer<typeof savingsAnalysisSchema>

export const buildingTypeSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  customerClass: z.string(),
})
export type BuildingType = z.infer<typeof buildingTypeSchema>

export const centerPointSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
})
export type CenterPoint = z.infer<typeof centerPointSchema>

export const climateZoneSchema = z.object({
  territoryId: z.number(),
  territoryName: z.string(),
  lseId: z.number(),
  lseName: z.string(),
  parentTerritoryId: z.number(),
  usageType: z.string(),
  itemTypes: z.string(),
  deregRes: z.boolean(),
  deregCandi: z.boolean(),
  centerPoint: centerPointSchema.optional(),
})
export type ClimateZone = z.infer<typeof climateZoneSchema>

export const propertySchema = z.object({
  keyName: z.string().nullable().optional(),
  dataType: z.string().nullable().optional(),
  dataValue: z.string().nullable().optional(),
})
export type Property = z.infer<typeof propertySchema>

export const factorsSchema = z.object({
  peakDemand: z.number(),
  monthlyConsumption: z.number(),
  annualConsumption: z.number(),
  meanAnnualConsumption: z.number(),
  meanBuildingArea: z.number(),
  meanIntensity: z.number(),
  buildingArea: z.number(),
})
export type Factors = z.infer<typeof factorsSchema>

export const measuresSchema = z.object({
  i: z.number(),
  v: z.number(),
})
export type Measures = z.infer<typeof measuresSchema>

export const baselineSchema = z.object({
  baselineId: z.string(),
  name: z.string(),
  buildingType: buildingTypeSchema,
  serviceType: z.string(),
  sourceId: z.string(),
  climateZone: climateZoneSchema.optional(),
  properties: z.array(propertySchema),
  factors: factorsSchema,
  measureDuration: z.number(),
  measureValue: z.string(),
  measureUnit: z.string(),
  startDay: z.number(),
  measures: z.array(measuresSchema),
})
export type Baseline = z.infer<typeof baselineSchema>

export const chargeItemSchema = z.object({
  fromDateTime: z.string(),
  toDateTime: z.string(),
  rateAmount: z.number(),
  itemQuantity: z.number(),
})

export const usageEstimateSchema = z.object({
  items: z.array(chargeItemSchema),
})

export type SavingsCalculationInput = {
  proposal?: Proposal | null
  utilityRate?: UtilityRate | null
  postSolarUtilityRate?: UtilityRate | null
  inverterEfficiency?: number | null
  loanProduct?: Omit<LoanProduct, "isVirtual"> | null
  quote?: Quote | null
  estimates?: Omit<EstimateNecessaryFields, "poisonId">[]
  savings?: SavingsAnalysis | null
  degradationPercentage?: number | null
  applyingPaydown?: boolean | null
  energyBackupData?: PowurOsEnergyBackupData | null
  name?: string
  postSolarName?: string
  reportingCallback?: (csv: string, name: string) => void
}

export const genabilityTariffRateBandSchema = z.object({
  tariffRateBandId: z.number(),
  tariffRateId: z.number().optional(),
  rateSequenceNumber: z.number(),
  hasConsumptionLimit: z.boolean(),
  hasDemandLimit: z.boolean(),
  hasPropertyLimit: z.boolean(),
  rateAmount: z.number().optional().nullable(),
  rateUnit: z.string().optional().nullable(),
  isCredit: z.boolean(),
  prevUpperLimit: z.number().optional().nullable(),
})

export type IGenabilityTariffRateBand = z.infer<
  typeof genabilityTariffRateBandSchema
>

export const energyReadingSchema = z.object({
  fromDateTime: z.union([z.date(), z.string()]),
  quantityUnit: z.string(),
  importQuantityValue: z.union([z.string(), z.number()]),
  exportQuantityValue: z.union([z.string(), z.number()]),
  toDateTime: z.union([z.date(), z.string()]),
  touType: z.string().optional().nullable(),
})

export const energyReadingsSchema = z.array(energyReadingSchema)

export type IEnergyReading = z.infer<typeof energyReadingSchema>

export const genabilitySeasonSchema = z.object({
  seasonId: z.number(),
  lseId: z.number(),
  seasonGroupId: z.number(),
  seasonName: z.string(),
  seasonFromMonth: z.number(),
  seasonFromDay: z.number(),
  seasonToMonth: z.number(),
  seasonToDay: z.number(),
})

export type IGenabilitySeason = z.infer<typeof genabilitySeasonSchema>

export const genabilityTouPeriodSchema = z.object({
  touPeriodId: z.number(),
  touId: z.number(),
  fromDayOfWeek: z.number(),
  fromHour: z.number(),
  fromMinute: z.number(),
  toDayOfWeek: z.number(),
  toHour: z.number(),
  toMinute: z.number(),
  calendarId: z.number().optional().nullable(),
})

export type IGenabilityTouPeriod = z.infer<typeof genabilityTouPeriodSchema>

const TOU_TYPES = [
  `CRITICAL_PEAK`,
  `OFF_PEAK`,
  `ON_PEAK`,
  `PARTIAL_PEAK`,
] as const

export const genabilityTouSchema = z.object({
  touId: z.number(),
  touGroupId: z.number(),
  lseId: z.number(),
  touName: z.string(),
  calendarId: z.number().optional().nullable(),
  isDynamic: z.boolean(),
  season: genabilitySeasonSchema.optional().nullable(),
  touType: z.enum(TOU_TYPES).optional().nullable(),
  touPeriods: z.array(genabilityTouPeriodSchema),
})

export type IGenabilityTou = z.infer<typeof genabilityTouSchema>

export const genabilityRateSchema = z.object({
  chargeClass: z.string().optional().nullable(),
  chargePeriod: z.string(),
  chargeType: z.string().optional().nullable(),
  fromDateTime: z.string(),
  rateBands: z.array(genabilityTariffRateBandSchema),
  rateGroupName: z.string(),
  rateName: z.string(),
  season: genabilitySeasonSchema.optional().nullable(),
  tariffId: z.number(),
  tariffRateId: z.number(),
  tariffSequenceNumber: z.number(),
  timeOfUse: genabilityTouSchema.optional().nullable(),
  toDateTime: z.string().optional().nullable(),
  variableRateKey: z.string().optional().nullable(),
  applicabilityKey: z.string().optional().nullable(),
})

export type IGenabilityRate = z.infer<typeof genabilityRateSchema>
